import { useState } from 'react';
import { IconSelector } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * DEV-1948/DEV-1949 — searchable multi-brand picker with saved brand groups.
 *
 * Fully controlled: the ONLY internal state is the popover's own open flag and
 * the search text. Selection lives on the page, because every other control on
 * the composer (preview counts, activity filter, the test gate) is invalidated
 * by a selection change and must see the same value the page sends.
 */

export interface BrandOption {
  id: string;
  label: string;
}

export interface BrandGroupOption {
  id: string;
  name: string;
  brandIds: string[];
}

interface BrandMultiSelectProps {
  options: BrandOption[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  groups: BrandGroupOption[];
  canEditGroups: boolean;
  onSaveGroup: () => void;
  onManageGroups: () => void;
  disabled?: boolean;
}

const matches = (haystack: string, needle: string): boolean =>
  haystack.toLowerCase().includes(needle.trim().toLowerCase());

export function BrandMultiSelect({
  options,
  selectedIds,
  onSelectionChange,
  groups,
  canEditGroups,
  onSaveGroup,
  onManageGroups,
  disabled = false,
}: BrandMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const triggerLabel = (): string => {
    if (selectedIds.length === 0) return 'Select brands...';
    if (selectedIds.length === 1) {
      const only = options.find((option) => option.id === selectedIds[0]);
      return only ? only.label : '1 brand selected';
    }
    return `${selectedIds.length} of ${options.length} brands selected`;
  };

  /** A selected brand stays visible while filtering — hiding it would make the
   * count in the header unexplainable and invite a blind "Clear all". Order is
   * always the server's order; selection never re-sorts the list, because a row
   * jumping under the cursor mid-click selects the wrong brand. */
  const visibleOptions = options.filter(
    (option) => !search.trim() || matches(option.label, search) || selectedIds.includes(option.id),
  );
  const visibleGroups = groups.filter((group) => !search.trim() || matches(group.name, search));

  const toggleBrand = (id: string) => {
    onSelectionChange(
      selectedIds.includes(id)
        ? selectedIds.filter((existing) => existing !== id)
        : [...selectedIds, id],
    );
  };

  /** Only the group's brands that are ACTUALLY available right now. A group can
   * outlive a brand being retired from `available-mvnos`, and applying an id
   * the composer cannot send is worse than applying fewer brands than the name
   * promises. */
  const liveIdsOf = (group: BrandGroupOption): string[] =>
    group.brandIds.filter((id) => options.some((option) => option.id === id));

  const applyGroup = (group: BrandGroupOption) => {
    const liveIds = liveIdsOf(group);
    if (liveIds.length === 0) return;
    const allSelected = liveIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      onSelectionChange(selectedIds.filter((id) => !liveIds.includes(id)));
      return;
    }
    onSelectionChange([...selectedIds, ...liveIds.filter((id) => !selectedIds.includes(id))]);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        // A stale filter on re-open hides brands the operator expects to see.
        if (!isOpen) setSearch('');
      }}
    >
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            className="w-full justify-between font-normal"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
          />
        }
      >
        <span className={selectedIds.length === 0 ? 'text-muted-foreground' : undefined}>
          {triggerLabel()}
        </span>
        <IconSelector className="ml-auto size-4 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--anchor-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            autoFocus
            placeholder="Search brands..."
            value={search}
            onValueChange={setSearch}
          />
          <div className="flex items-center justify-between gap-2 px-2 py-1.5">
            <span className="text-xs text-muted-foreground">
              {selectedIds.length} of {options.length} selected
            </span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onSelectionChange(options.map((option) => option.id))}
                disabled={selectedIds.length === options.length}
              >
                Select all
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onSelectionChange([])}
                disabled={selectedIds.length === 0}
              >
                Clear all
              </Button>
            </div>
          </div>
          <CommandList>
            <CommandEmpty>No brands match "{search}"</CommandEmpty>
            {visibleGroups.length > 0 && (
              <CommandGroup heading="Saved groups">
                {visibleGroups.map((group) => {
                  const liveIds = liveIdsOf(group);
                  const allSelected =
                    liveIds.length > 0 && liveIds.every((id) => selectedIds.includes(id));
                  const someSelected =
                    !allSelected && liveIds.some((id) => selectedIds.includes(id));
                  return (
                    <CommandItem
                      key={`group-${group.id}`}
                      value={`group-${group.id}`}
                      disabled={liveIds.length === 0}
                      aria-checked={allSelected}
                      onSelect={() => applyGroup(group)}
                    >
                      <Checkbox
                        className="pointer-events-none"
                        checked={allSelected}
                        indeterminate={someSelected}
                      />
                      <span className="flex-1">{group.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {liveIds.length === 0
                          ? '(no available brands)'
                          : `${liveIds.length} brands`}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {visibleGroups.length > 0 && visibleOptions.length > 0 && <CommandSeparator />}
            {visibleOptions.length > 0 && (
              <CommandGroup heading="Brands">
                {visibleOptions.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    aria-checked={selectedIds.includes(option.id)}
                    onSelect={() => toggleBrand(option.id)}
                  >
                    {/* Non-interactive: the row owns the click, so a click on
                        the box itself must not toggle twice (once via the
                        checkbox, once via the row's onSelect). */}
                    <Checkbox
                      className="pointer-events-none"
                      checked={selectedIds.includes(option.id)}
                    />
                    <span className="flex-1">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          {canEditGroups && (
            <div className="flex items-center justify-between gap-2 border-t border-border px-2 py-1.5">
              <Button
                variant="ghost"
                size="xs"
                disabled={selectedIds.length === 0}
                title={
                  selectedIds.length === 0
                    ? 'Select at least one brand to save it as a group'
                    : undefined
                }
                onClick={() => {
                  setOpen(false);
                  onSaveGroup();
                }}
              >
                Save as group
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  setOpen(false);
                  onManageGroups();
                }}
              >
                Manage groups
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
